import React, { useState } from "react";
import cx from "classnames";
import styles from "./PatientSelector.module.scss";
import { FormProvider, useController, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import * as paths from "features/routing/paths";
import { Button } from "components/Button";
import { patientQuestionareRequest } from "api/patientQuestionare";
import { patientQuestionRequest } from "api/patientQuestionRequest";
import { patientQuestionType } from "types/patient";
import { CiamTextInput } from "components/TextInput";
import { RadioButton, CAIMRadioButtonGroup, CAIMRadioButton } from "components/RadioButton";
import { PatientContainer } from "./PatientContainer";
import { isValidZipCIAM } from "utils/zipValidation";


interface PatientQuestionsProps {
  patient: any | null;
  idToken: string | null;
  hideQuestionForm: (verified?: boolean) => void;
  isVerified: boolean;
  submitCountParent: number;
}

export const PatientQuestions: React.FC<PatientQuestionsProps> = ({
  patient,
  idToken,
  hideQuestionForm,
  isVerified,
  submitCountParent,
}) => {
  const formMethods = useForm<patientQuestionType>({
    mode: "onBlur",
  });
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    setValue,
    getValues,
    trigger,
  } = formMethods;
  const [isSubmitLogin, setIsSubmitLogin] = React.useState<boolean>(false);
  const [formError, setFormError] = React.useState<boolean>(false);
  const [submitCount, setSubmitCount] = React.useState<number>(0);
  const [submitLocked, setSubmitLocked] = React.useState(false);
  const [executeOnce, setExecuteOnce] = React.useState(false);
  const [isReady, setIsReady] = React.useState(false);
  const [patientQuestion, setPatientQuestion] = React.useState<any>(null);

  const zipcode = register("zipcode", { validate: isValidZipCIAM });

  const lastExam = register("lastExam", {
      required: "Please select an exam month and year",
  });

  const lastOfficeVisit = register("lastOfficeVisit", {
      required: "Please select a location",
  });

  const onSubmitNew = async (data: patientQuestionType) => {
    if (
      data.lastExam &&
      data.zipcode &&
      data.lastOfficeVisit &&
      patient
    ) {
        try {
          setSubmitLocked(true);
          const confirmQuestion = await patientQuestionRequest({
            zipCode: data.zipcode,
            lastExam: data.lastExam,
            lastOfficeVisit: data.lastOfficeVisit,
            patient: patient,
            patientQuestion: patientQuestion,
            submitCount: submitCount + 1,
            id_token: idToken
          });
          setSubmitLocked(false);
          setSubmitCount(submitCount + 1);
          setFormError(true);
          if(confirmQuestion && confirmQuestion.is_verified) {
            hideQuestionForm(true);
          }
          setValue("zipcode", "");
          setValue("lastExam", "");
          setValue("lastOfficeVisit", "");
        } catch (e) {
          //showNoPatient();
          setSubmitLocked(false);
          setFormError(true);
          throw e;
        }
    }
  };

  React.useEffect(() => {
    if(isVerified)
      setIsReady(true);
    if(!patient || isVerified || executeOnce)
      return;

    const getQuestionaries = async () => {      
      try {
        const patientQ = await patientQuestionareRequest({
          patient_uid : patient.PatientUid
        });
        if(!isVerified && submitCountParent < 3)
          setSubmitCount(submitCountParent);
          setPatientQuestion(patientQ.questions);
          setExecuteOnce(true);
          setIsReady(true);
      } catch (e) {
        // TODO: There is a race condition in axios's implementation of cancel tokens
        // that can cause the thrown object to not contain the relevant state. This should
        // be fixed in the next release of axios (see https://github.com/axios/axios/pull/3305)
        console.log(e);
        //showNoPatient();
        //showErrorToast();
      }
    }        
    getQuestionaries();

  },[patient, submitCountParent, executeOnce]);

  if(!patient || !isReady)
    return false;

  return (
    <div>
      <div className={styles.cardContainer}>
        <div className={styles.ConfirmDataForm}>
        </div>
        <div>
          {(patientQuestion && submitCount < 3) && (
            <>
              <h1 className={styles.heading}>Patient Verification</h1>
              <p className={styles.paragraph}>
                We've identified an account that partially matches the details you provided. To make sure it's really yours, please enter the correct answer to the following questions:
              </p>
            </>
          )}
          {(!patientQuestion || submitCount >= 3) && (
            <>
              <h1 className={styles.heading}>Patient Verification Failed</h1>
              <p className={cx(styles.paragraph,styles.epFont)}>
                We prioritize patient data security. Based on the responses you provided on the previous page, we weren't able to match the information in our system. Often this happens if your email addresses, phone number, or your last name have since your last MyEyeDr. visit.
              </p>
              <p className={cx(styles.paragraph, styles.epFont)}>
                You can try again in seven (7) days by logging back into this system.
              </p>
            </>
          )}
          <div className={styles.divider}></div>
          {(patientQuestion && submitCount < 3) && (
            <div>
              <div className={styles.qContainer}>
                <div className={styles.qNumber}>1</div>
                <div className={styles.qLabel}>What was your 5-digit ZIP code at the time of your last MyEyeDr exam?</div>
              </div>
              <CiamTextInput
                {...zipcode}
                maxLength={5}
                className={styles.textInputShow}
                inputType="text"
                disabled= { submitCount > 3 ? true: false }
                showError={(formError)? false : true}
                onChange={(e) => {
                  zipcode.onChange(e);
                }}
                placeholder=""
                errorMessage={errors.zipcode?.message}
                defaultValue=""
              ></CiamTextInput>
              <div className={styles.divider}></div>  
              <div className={styles.qContainer}>
                <div className={styles.qNumber}>2</div>
                <div className={styles.qLabel}>Month & year of last MyEyeDr.  exam:</div>
              </div>
              <CAIMRadioButtonGroup
                className={styles.buttonGroup}
                errorMessage={errors.lastExam?.message}
                errorClassName={cx({
                  [styles.emptyError]: !errors.lastExam,
                })}
              >
                {
                  patientQuestion.LastExamDateOptions.map((examDate, i) => {
                    return (
                      <>  
                        <RadioButton
                          {...lastExam}
                          className={styles.radioButton}
                          value={examDate}
                          disabled={submitCount > 3 ? true: false }
                          hasError={!!errors.lastExam}
                        >
                          {examDate}
                        </RadioButton>
                      </> 
                    )
                  })
                }
              </CAIMRadioButtonGroup>
              <div className={styles.divider}></div>  
              <div className={styles.qContainer}>
                <div className={styles.qNumber}>3</div>
                <div className={styles.qLabel}>Location of most recent MyEyeDr. exam:</div>
              </div>
              <CAIMRadioButtonGroup
                className={styles.buttonGroup}
                errorMessage={errors.lastOfficeVisit?.message}
                errorClassName={cx({
                  [styles.emptyError]: !errors.lastExam,
                })}
              >
                {
                  patientQuestion.LastExamAddOptions.map((officeVisit, i) => {
                    return (
                      <>  
                        <RadioButton
                          {...lastOfficeVisit}
                          className={styles.radioButton}
                          value={officeVisit}
                          disabled={submitCount > 3 ? true: false }
                          hasError={!!errors.lastOfficeVisit}
                        >
                          {officeVisit}
                        </RadioButton>
                      </> 
                    )
                  })
                }
              </CAIMRadioButtonGroup>   
              <div className={styles.divider}></div> 
              <Button 
                text="CONFIRM"
                onClick={handleSubmit(onSubmitNew)} 
                className={styles.submit}
                disabled={submitLocked || submitCount > 3 ? true: false}
                loading={submitLocked}
              >            
              </Button>
              { formError && (
                <div>
                  <p className={styles.errorGlobalQuest}>We weren't able to match your responses with our records. Please check your responses and try again. You have only {3 - submitCount} {(3 - submitCount === 1)? " attempt ": " attempts "}remaining.</p>
                </div>
              )}           
            </div>
          )}
          {(!patientQuestion || submitCount >= 3) && (
            <div className={styles.patientList}>
              <PatientContainer
                patient={patient}
                index={1}
                masked={false}
                errorContainer={true}
                idToken={idToken}
                calledFrom="Question"
              />
            </div>
          )}
      </div>
      </div>
      <div className={styles.bannerContainer}>
        <div>
          <h2 className={styles.bannerHeading}>
            Need to make changes to your account or personal information?
          </h2>
          <p className={styles.bannerText}>
            If you need to add or update any of the information you see 
            about your MyEyeDr account, please{" "}
            <a
              href="https://locations.myeyedr.com/search"
              className={styles.bannerLink}
            >
              contact your nearest MyEyeDr location
            </a> for assistance.
          </p>
        </div>
      </div>
    </div>
  
  );
};
